import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, ExternalLink, QrCode, UserPlus, X } from 'lucide-react'
import { signInApi, type SignInItemDto, type SignInResolution, type SignInTarget } from '@/api/signin'
import { Button } from '@/components/ui/button'
import { Qr, signInUrl } from './Qr'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

function errorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data
  return data?.detail ?? data?.title ?? 'Something went wrong. Please try again.'
}

const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

/** A selfie someone took at sign-in, fetched with the trainer's login rather than from a public address. */
function SelfieThumb({ signInId }: { signInId: string }) {
  const photo = useQuery({ queryKey: ['signin-photo', signInId], queryFn: () => signInApi.photo(signInId), staleTime: Infinity, retry: false })
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!photo.data) return
    const u = URL.createObjectURL(photo.data)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [photo.data])
  return url
    ? <img src={url} alt="" className="h-12 w-12 rounded-full object-cover border border-gray-200 shrink-0" />
    : <div className="h-12 w-12 rounded-full bg-gray-100 shrink-0" />
}

const STATUS_TEXT: Record<string, string> = {
  MatchedByEmail: 'marked present',
  Confirmed: 'confirmed and marked present',
  WalkInAdded: 'added as a walk-in',
  Dismissed: 'dismissed',
}

/**
 * Attendance sign-in on a booking or session register: open a window, show the QR code, and deal with anyone who
 * signed in but wasn't matched to a booked attendee. Used by trainers and admins alike.
 */
export function SignInPanel({
  target, hidden = false, onChanged,
}: {
  target: SignInTarget
  hidden?: boolean
  /** Called when sign-ins change the register (someone marked present), so the page can reload it. */
  onChanged?: () => void
}) {
  const qc = useQueryClient()
  const key = ['signin-status', target.bookingId ?? target.sessionId]
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [big, setBig] = useState(false)

  const status = useQuery({
    queryKey: key,
    queryFn: () => signInApi.status(target),
    enabled: !hidden,
    // While it is open the page keeps itself up to date, so people appear as they sign in.
    refetchInterval: q => (q.state.data?.window?.isOpen ? 4000 : false),
  })
  const data = status.data
  const win = data?.window
  const open = !!win?.isOpen

  // When the number of people recorded changes, the register above needs to reload too.
  const seen = useRef<number | null>(null)
  useEffect(() => {
    if (!data) return
    const count = data.signedIn
    if (seen.current !== null && seen.current !== count) onChanged?.()
    seen.current = count
  }, [data, onChanged])

  const refresh = () => { qc.invalidateQueries({ queryKey: key }); onChanged?.() }
  const fail = (e: unknown) => setError(errorMessage(e))

  const openMutation = useMutation({ mutationFn: () => signInApi.open(target), onSuccess: () => { setError(null); refresh() }, onError: fail })
  const closeMutation = useMutation({ mutationFn: () => signInApi.close(win!.id), onSuccess: () => { setError(null); refresh() }, onError: fail })
  const resolveMutation = useMutation({
    mutationFn: (v: { id: string; how: SignInResolution; attendeeId?: string; existingStaffMemberId?: string }) =>
      signInApi.resolve(v.id, v.how, { attendeeId: v.attendeeId, existingStaffMemberId: v.existingStaffMemberId }),
    onSuccess: () => { setError(null); refresh() },
    onError: fail,
  })

  if (hidden || status.isError) return null
  if (!data) return null

  const url = win ? signInUrl(win.code) : ''
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* no clipboard access */ }
  }

  const pendingRow = (s: SignInItemDto) => (
    <li key={s.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
      <div className="flex items-start gap-3">
        {s.hasPhoto && <SelfieThumb signInId={s.id} />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900">{s.firstName} {s.lastName}</p>
          <p className="text-xs text-gray-500 break-all">{s.email} · {clock(s.signedInAt)}</p>
          <p className="text-xs text-amber-800 mt-0.5">
            {s.status === 'NeedsReview' ? 'Not an exact match. Is this one of these people?' : 'Not on the list.'}
            {s.photoSkipped && ' They couldn’t take a photo.'}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {s.candidates.map(c => (
          <Button key={c.id} size="sm" variant="outline" disabled={resolveMutation.isPending}
            onClick={() => resolveMutation.mutate({ id: s.id, how: 'Confirm', attendeeId: c.id })}>
            <Check size={13} className="mr-1.5" /> That&apos;s {c.name}
          </Button>
        ))}
        {data.canAddWalkIn && (
          <Button size="sm" variant="outline" disabled={resolveMutation.isPending}
            onClick={() => resolveMutation.mutate({ id: s.id, how: 'WalkIn', existingStaffMemberId: s.suggestedStaff?.id })}>
            <UserPlus size={13} className="mr-1.5" />
            {s.suggestedStaff ? `Add ${s.suggestedStaff.name} as a walk-in` : 'Add as a walk-in'}
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={resolveMutation.isPending}
          onClick={() => resolveMutation.mutate({ id: s.id, how: 'Dismiss' })}>
          <X size={13} className="mr-1.5" /> Dismiss
        </Button>
      </div>
      {!data.canAddWalkIn && s.status === 'Unmatched' && (
        <p className="text-xs text-gray-600">Public sessions can&apos;t take walk-ins here. Speak to them in person, then dismiss this.</p>
      )}
    </li>
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <QrCode size={15} className="text-indigo-500" />
            Live attendance
          </CardTitle>
          {open
            ? <Button size="sm" variant="outline" onClick={() => closeMutation.mutate()} disabled={closeMutation.isPending}>Close sign-in</Button>
            : <Button size="sm" onClick={() => openMutation.mutate()} disabled={openMutation.isPending}>{win ? 'Open sign-in again' : 'Open sign-in'}</Button>}
        </div>
        {!open && (
          <p className="text-sm text-gray-500">
            Attendees scan a QR code and enter their name and email{data.photoMode === 'none' ? '' : data.photoMode === 'required' ? ', plus a photo' : ', and can add a photo'}.
            Anyone whose email matches the register is marked present straight away; everyone else comes to you to check.
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

        {open && win && (
          <div className="flex flex-wrap items-center gap-5">
            <button type="button" onClick={() => setBig(b => !b)} className="shrink-0" title="Click to enlarge" aria-label="Enlarge QR code">
              <Qr url={url} size={big ? 320 : 140} />
            </button>
            <div className="space-y-2 min-w-0">
              <p className="text-3xl font-semibold text-gray-900">
                {data.signedIn}<span className="text-lg font-normal text-gray-500"> of {data.expected} signed in</span>
              </p>
              <p className="text-xs text-gray-500">Closes at {clock(win.closesAt)} · or when you close it</p>
              <div className="flex items-center gap-2 min-w-0">
                <code className="text-xs bg-gray-100 rounded px-2 py-1 truncate">{url}</code>
                <Button size="sm" variant="ghost" onClick={copy}>{copied ? <Check size={13} /> : <Copy size={13} />}</Button>
              </div>
              <Button size="sm" variant="outline" onClick={() => window.open(`/signin-display/${win.code}`, '_blank', 'noopener')}>
                <ExternalLink size={13} className="mr-1.5" /> Show QR on screen
              </Button>
            </div>
          </div>
        )}

        {!open && win && data.signedIn > 0 && (
          <p className="text-sm text-gray-600">{data.signedIn} of {data.expected} signed in.</p>
        )}

        {data.pending.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-900">Needs your attention ({data.pending.length})</p>
            <ul className="space-y-2">{data.pending.map(pendingRow)}</ul>
          </div>
        )}

        {data.recent.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-gray-600">Recent sign-ins ({data.recent.length})</summary>
            <ul className="mt-2 space-y-1">
              {data.recent.map(s => (
                <li key={s.id} className="flex flex-wrap justify-between gap-x-3 text-gray-700">
                  <span>{s.firstName} {s.lastName}{s.recordedAs && s.recordedAs !== `${s.firstName} ${s.lastName}` ? <span className="text-gray-400"> as {s.recordedAs}</span> : null}</span>
                  <span className="text-xs text-gray-500">{STATUS_TEXT[s.status] ?? s.status} · {clock(s.signedInAt)}{s.photoSkipped ? ' · no photo' : ''}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  )
}
